<script>
  import { format } from 'svelte-i18n';
  import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { LockAllToggle } from '@client/components/lock-all-toggle';

  import { DIRECTORY_FIELDS_DEFAULTS, DIRECTORY_FIELDS_SYSTEMS, DIRECTORY_TRANSFORM_PRESETS } from '@client/enums';

  import { deepCopyArray } from '@common/utils/arrays';

  export let fields = [];
  export let framed = true;
  export let namePlaceholder = $format('label.NAME');
  export let valuePlaceholder = $format('label.VALUE');
  export let readOnly = false;

  let dragStartIndex = null;
  let dragOverIndex = null;
  let dragFromHandle = false;
  let openPresetIndex = null;

  const toggleFieldValue = (index, field) => {
    if (canEditDetails) {
      fields[index][field] = !fields[index][field];
      fields = [...fields];

      return true;
    }

    return false;
  };

  const toggleAllLocks = () => {
    if (!canEditDetails) return;
    const next = !allLocked;
    fields.forEach((field) => (field.forceoverride = next));
    fields = [...fields];
  };

  const addTransform = (index) => {
    fields[index].transforms.push({
      regexp: '',
      change: '',
      flags: ''
    });

    fields = [...fields];
  };

  const PRESET_GROUP_ORDER = [
    { id: 'phone', labelKey: 'label.TRANSFORM_PRESET_GROUP_PHONE' },
    { id: 'office', labelKey: 'label.TRANSFORM_PRESET_GROUP_OFFICE' },
    { id: 'text', labelKey: 'label.TRANSFORM_PRESET_GROUP_TEXT' }
  ];

  const applyPreset = (index, presetId) => {
    const preset = DIRECTORY_TRANSFORM_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    if (fields[index].transforms.length && !window.confirm($format('label.TRANSFORM_PRESET_OVERWRITE_CONFIRM'))) {
      return;
    }

    fields[index].transforms = deepCopyArray(preset.transforms);
    fields = [...fields];
  };

  const addField = () => {
    fields.push({
      system: '',
      source: '',
      dest: '',
      transforms: [],
      forceoverride: true
    });

    fields = [...fields];
  };

  const removeField = (index) => {
    fields.splice(index, 1);
    fields = [...fields];
  };

  const removeTransform = (fieldIndex, transformIndex) => {
    fields[fieldIndex].transforms.splice(transformIndex, 1);
    fields = [...fields];
  };

  const copyFields = (type) => {
    fields = [...fields, ...deepCopyArray(DIRECTORY_FIELDS_DEFAULTS[type])];
  };

  const onDrop = (targetIndex) => {
    if (dragStartIndex === null || dragStartIndex === targetIndex) return;
    const newFields = [...fields];
    const [moved] = newFields.splice(dragStartIndex, 1);
    newFields.splice(targetIndex, 0, moved);
    fields = newFields;
  };

  $: canEditDetails = !readOnly;
  $: allLocked = fields.length > 0 && fields.every((field) => field.forceoverride);
</script>

<div class="fields" class:framed class:readonly={readOnly}>
  <div class="headers">
    {#if canEditDetails}<div />{/if}
    <div class="system">{$format('label.SOURCE')}</div>
    {#if canEditDetails && fields.length}
      <LockAllToggle
        active={allLocked}
        tooltipActive="label.UNLOCK_ALL_FIELDS"
        tooltipInactive="label.LOCK_ALL_FIELDS"
        id="directory-fields-lock-all"
        on:toggle={toggleAllLocks}
      />
    {:else}
      <div />
    {/if}
    <div class="name">{$format('label.ORIGINAL')}</div>
    <div class="value">{$format('label.DESTINATION')}</div>
  </div>
  {#each fields as field, fieldIndex}
    <div
      class="field-group"
      class:dragging={dragStartIndex === fieldIndex}
      class:drag-over={dragOverIndex === fieldIndex && dragStartIndex !== fieldIndex}
      draggable={canEditDetails}
      on:dragstart={(e) => {
        if (!dragFromHandle) {
          e.preventDefault();
          return;
        }
        dragStartIndex = fieldIndex;
      }}
      on:dragover|preventDefault={() => {
        dragOverIndex = fieldIndex;
      }}
      on:dragleave={() => {
        if (dragOverIndex === fieldIndex) dragOverIndex = null;
      }}
      on:drop={() => {
        onDrop(fieldIndex);
        dragStartIndex = null;
        dragOverIndex = null;
      }}
      on:dragend={() => {
        dragStartIndex = null;
        dragOverIndex = null;
        dragFromHandle = false;
      }}
    >
      <div class="columns">
        {#if canEditDetails}
          <Icon
            name="grip-vertical"
            color="secondary"
            class="handle cursor-move user-select-none text-medium"
            on:mousedown={() => (dragFromHandle = true)}
            on:mouseup={() => (dragFromHandle = false)}
          />
        {/if}

        {#if canEditDetails}
          <select class="system" type="text" bind:value={field.system}>
            {#each Object.keys(DIRECTORY_FIELDS_SYSTEMS) as system}
              <option value={system}>{$format(DIRECTORY_FIELDS_SYSTEMS[system])}</option>
            {/each}
          </select>
        {:else}
          <div class="system">{$format(DIRECTORY_FIELDS_SYSTEMS[field.system])}</div>
        {/if}

        {#if field.forceoverride}
          <Icon
            name="lock"
            id="param-field-{fieldIndex}-lock"
            color="danger"
            class="text-medium {canEditDetails ? 'pointer' : ''}"
            on:click={() => toggleFieldValue(fieldIndex, 'forceoverride')}
            role="button"
            tabindex="0"
          />
          <Tooltip target="param-field-{fieldIndex}-lock" placement="top">
            {$format('label.FIELD_LOCKED_BY_ADMIN')}
          </Tooltip>
        {:else}
          <Icon
            name="lock-open"
            id="param-field-{fieldIndex}-unlock"
            class="text-medium {canEditDetails ? 'pointer' : ''}"
            on:click={() => toggleFieldValue(fieldIndex, 'forceoverride')}
            role="button"
            tabindex="0"
          />
          <Tooltip target="param-field-{fieldIndex}-unlock" placement="top">
            {$format('label.FIELD_CAN_BE_MODIFIED')}
          </Tooltip>
        {/if}

        {#if canEditDetails}
          <input type="text" class="name" placeholder={namePlaceholder} bind:value={field.source} />
          <input type="text" class="value" placeholder={valuePlaceholder} bind:value={field.dest} />
        {:else}
          <div class="name">{field.source}</div>
          <div class="value">{@html field.dest || '&nbsp;'}</div>
        {/if}

        {#if canEditDetails}
          <Icon
            name="trash"
            class="delete-field pointer"
            on:click={() => removeField(fieldIndex)}
            role="button"
            tabindex="0"
            data-testid="remove-field-{fieldIndex}"
          />
        {/if}
      </div>
      <div class="transform-fields" class:hide={readOnly && !field.transforms.length}>
        <div class="transform-fields-label">
          <b>{field.source}</b>
          {$format('label.TRANSFORMS_LENGTH', { values: { length: field.transforms.length } })}
        </div>
        {#each field.transforms as transform, transformIndex}
          {#if canEditDetails}
            <div class="columns">
              <input type="text" class="regexp" placeholder="Pattern" bind:value={transform.regexp} />
              <input type="text" class="flags" placeholder="Flags" bind:value={transform.flags} />
              <input type="text" class="replace" placeholder="Replace with" bind:value={transform.change} />
              <Icon
                name="trash"
                class="pointer"
                on:click={() => removeTransform(fieldIndex, transformIndex)}
                role="button"
                tabindex="0"
                data-testid="remove-field-{fieldIndex}-transform-{transformIndex}"
              />
            </div>
          {:else}
            <div class="field columns">
              <div class="regexp">{transform.regexp}&nbsp;</div>
              <div class="flags">{transform.flags}&nbsp;</div>
              <div class="replace">{transform.change}&nbsp;</div>
            </div>
          {/if}
        {/each}
        {#if canEditDetails}
          <div class="transform-preset-actions {field.transforms.length ? 'mt-3' : ''}">
            <Button
              color="secondary"
              size="sm"
              class="pointer"
              data-testid="add-field-{fieldIndex}-transform"
              on:click={() => addTransform(fieldIndex)}
            >
              <Icon name="plus" />
              <span>{$format('label.TRANSFORM')}</span>
            </Button>
            <Dropdown
              isOpen={openPresetIndex === fieldIndex}
              toggle={() => (openPresetIndex = openPresetIndex === fieldIndex ? null : fieldIndex)}
            >
              <DropdownToggle color="secondary" size="sm" caret data-testid="apply-preset-{fieldIndex}">
                {$format('label.TRANSFORM_PRESET_APPLY')}
              </DropdownToggle>
              <DropdownMenu>
                {#if openPresetIndex === fieldIndex}
                  {#each PRESET_GROUP_ORDER as group}
                    <DropdownItem header>{$format(group.labelKey)}</DropdownItem>
                    {#each DIRECTORY_TRANSFORM_PRESETS.filter((preset) => preset.group === group.id) as preset}
                      <DropdownItem
                        id="preset-{fieldIndex}-{preset.id}"
                        data-testid="apply-preset-{fieldIndex}-{preset.id}"
                        on:click={() => applyPreset(fieldIndex, preset.id)}
                      >
                        {$format(preset.labelKey)}
                      </DropdownItem>
                    {/each}
                  {/each}
                {/if}
              </DropdownMenu>
            </Dropdown>
            {#if openPresetIndex === fieldIndex}
              {#each DIRECTORY_TRANSFORM_PRESETS as preset}
                <Tooltip target="preset-{fieldIndex}-{preset.id}" placement="right">
                  {$format(preset.tipKey)}
                </Tooltip>
              {/each}
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/each}
  {#if canEditDetails}
    <Button
      color="secondary"
      size="sm"
      class="pointer {fields.length ? 'mt-3' : ''}"
      on:click={addField}
      data-testid="add-field"
    >
      <Icon name="plus" />
      <span>{$format('label.FIELD')}</span>
    </Button>
    <div class="add-field-presets">
      <Button color="secondary" size="sm" on:click={() => copyFields('microsoft')}
        >{$format('label.ADD_MICROSOFT_FIELDS')}</Button
      >
      <Button color="secondary" size="sm" on:click={() => copyFields('google')}
        >{$format('label.ADD_GOOGLE_FIELDS')}</Button
      >
      <Button color="secondary" size="sm" on:click={() => copyFields('bamboo')}
        >{$format('label.ADD_BAMBOO_FIELDS')}</Button
      >
    </div>
  {/if}
</div>

<style lang="scss" src="./directory-fields.scss"></style>
